// @ts-strict-ignore
import React from 'react';
import { ReleaseComponentProps } from '../typescriptHelpers';
import Popover from 'antd/lib/popover';
import { runwayColors, antGrey, tagStyle, EXTERNAL_EVENT_BACKGROUND_COLOR } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import {
    displayLifecycleEventFormattedTitle,
    formatDateString,
    getCalendarEventPopoverIcon,
    getCalendarEventDescription,
    formatStartAndEndHourString,
    displayEventManuallyUpdatedBy,
} from '../helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Icon, { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import {
    LifecycleEventType,
    AutoReleaseStrategy,
    ReleaseCalendarEventType,
    AutomationRun,
    AutomationType,
    AutoSubmitStrategiesOnOrAfterTargetDate,
    IntegrationType,
} from '../types';
import Tag from 'antd/lib/tag';
import { useSelector } from 'react-redux';
import { selectScheduleSettings, selectUserById } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { capitalizeFirstLetter } from '@/helpers';
import MarkdownComponent from '@/components/HOCs/Markdown';
import rehypeRaw from 'rehype-raw'; // Import the plugin

interface ReleaseCalendarEventPopoverProps extends ReleaseComponentProps {
    backgroundColor: string;
    displayVersion?: string;
    eventContent: React.ReactNode;
    lifecycleAutomations?: AutomationRun[];
    eventType: LifecycleEventType;
    eventDateType: ReleaseCalendarEventType;
    eventDate: string;
    isEventComplete: boolean;
    isTargetDateScheduledFromCadence: boolean;
    targetDateManuallyUpdatedAt?: string;
    targetDateManuallyUpdatedByUserId?: string;
    incrementDayNumber?: number;
    stagedRolloutPercentage?: number;
    isSkippedRelease: boolean;
    title?: string;
    isExternalEvent: boolean;
    externalEventDescription?: string;
    organizer?: string;
    titleIcon?: React.ReactNode;
    integrationType?: IntegrationType;
    eventEndDate?: string;
    timezone?: string;
    allDay?: boolean;
    shouldAutoAccelerateBasedOnPhasedReleaseDay?: boolean;
}

const ReleaseCalendarEventPopover: React.FC<ReleaseCalendarEventPopoverProps> = (
    props: ReleaseCalendarEventPopoverProps,
) => {
    const scheduleCadenceSettings = useSelector((state: RootState) => selectScheduleSettings(state, props.appId));
    const manualUpdatedByUser = useSelector((state: RootState) =>
        props.targetDateManuallyUpdatedByUserId ?
            selectUserById(state, props.orgId, props.appId, props.targetDateManuallyUpdatedByUserId)
        :   null,
    );
    const autoSubmitAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoSubmitRelease] ?
            props.lifecycleAutomations[AutomationType.AutoSubmitRelease]
        :   null;
    const autoReleaseAutomation =
        props.lifecycleAutomations && props.lifecycleAutomations[AutomationType.AutoRelease] ?
            props.lifecycleAutomations[AutomationType.AutoRelease]
        :   null;

    const isEventComplete = props.isEventComplete;
    let eventTitle =
        props.title ?
            props.title
        :   displayLifecycleEventFormattedTitle(props.eventType, props.eventDateType, isEventComplete);
    eventTitle = capitalizeFirstLetter(eventTitle);

    const shouldDisplayEndDateForType =
        props.eventType != LifecycleEventType.LifecycleEventTypeKickoff &&
        props.eventType != LifecycleEventType.LifecycleEventTypeSubmit &&
        props.eventType != LifecycleEventType.LifecycleEventTypeRelease &&
        props.eventType != LifecycleEventType.LifecycleEventTypeRolloutStart &&
        props.eventType != LifecycleEventType.LifecycleEventTypeRolloutIncrement &&
        props.eventType != LifecycleEventType.LifecycleEventTypeRolloutCompletion;

    const [formattedStartDateHour, formattedEndDateHour] = formatStartAndEndHourString(
        props.eventDate,
        shouldDisplayEndDateForType,
        shouldDisplayEndDateForType ? props.eventEndDate : null,
        props.timezone,
        props.allDay,
    );

    const shouldDisplayEndDate = props.eventEndDate && formattedEndDateHour && shouldDisplayEndDateForType;

    if (
        props.eventType === LifecycleEventType.LifecycleEventTypeRolloutIncrement &&
        props.incrementDayNumber &&
        props.stagedRolloutPercentage
    ) {
        if (props.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate) {
            eventTitle = `${eventTitle} - Day ${props.incrementDayNumber} (${props.stagedRolloutPercentage}%)`;
        } else if (props.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate) {
            eventTitle = `${eventTitle} (${props.stagedRolloutPercentage}%)`;
        }
    }

    if (props.eventType === LifecycleEventType.LifecycleEventTypeRolloutStart && props.stagedRolloutPercentage) {
        eventTitle = `${eventTitle} (${props.stagedRolloutPercentage}%)`;
    }

    const title = (
        <div
            style={{
                margin: 0,
                padding: 8,
                backgroundColor: props.isExternalEvent ? EXTERNAL_EVENT_BACKGROUND_COLOR : props.backgroundColor,
                width: '100%',
                height: '100%',
                boxSizing: 'border-box',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
            }}
        >
            <span>
                {props.isExternalEvent ?
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Text
                            style={{
                                color: runwayColors.white,
                                fontSize: 14,
                                fontWeight: 'normal',
                            }}
                        >
                            {eventTitle}
                        </Text>
                        {props.titleIcon}
                    </div>
                :   <div style={{ color: runwayColors.white, fontSize: 14, fontWeight: 'normal' }}>
                        {props.displayVersion && (
                            <Tag
                                color={runwayColors.white}
                                style={{ ...tagStyle, border: `1px solid ${runwayColors.white}` }}
                            >
                                <Text style={{ color: props.backgroundColor }}>{props.displayVersion}</Text>
                            </Tag>
                        )}

                        {eventTitle}
                    </div>
                }
            </span>
        </div>
    );

    const content = () => {
        const submitEnabledNoEarlierThanTarget =
            props.eventType === LifecycleEventType.LifecycleEventTypeSubmit &&
            autoSubmitAutomation &&
            autoSubmitAutomation.enabled &&
            AutoSubmitStrategiesOnOrAfterTargetDate.includes(autoSubmitAutomation.data.submitStrategy);
        const releaseEnabledNoEarlierThanTarget =
            props.eventType === LifecycleEventType.LifecycleEventTypeRelease &&
            autoReleaseAutomation &&
            autoReleaseAutomation.enabled &&
            autoReleaseAutomation.data.strategy === AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate;

        const isAcceleratedRollout =
            props.shouldAutoAccelerateBasedOnPhasedReleaseDay && props.stagedRolloutPercentage === 100;

        const icon = getCalendarEventPopoverIcon(
            props.eventType,
            props.eventDateType,
            props.eventDate,
            submitEnabledNoEarlierThanTarget || releaseEnabledNoEarlierThanTarget,
            props.isTargetDateScheduledFromCadence,
            isAcceleratedRollout,
        );

        const description = getCalendarEventDescription(
            props.eventType,
            props.eventDateType,
            submitEnabledNoEarlierThanTarget || releaseEnabledNoEarlierThanTarget,
            scheduleCadenceSettings,
            props.isTargetDateScheduledFromCadence,
            props.isSkippedRelease,
            props.integrationType,
            isAcceleratedRollout,
        );

        const showManualUpdate =
            props.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate &&
            !!props.targetDateManuallyUpdatedAt;
        const manualUpdateText = displayEventManuallyUpdatedBy(props.targetDateManuallyUpdatedAt, manualUpdatedByUser);

        return (
            <>
                <div
                    style={{
                        paddingLeft: 10,
                        paddingRight: 10,
                        paddingTop: 10,
                        paddingBottom: shouldDisplayEndDate ? 5 : 10,
                    }}
                >
                    {icon && (
                        <Icon
                            component={icon}
                            style={{
                                color: isAcceleratedRollout ? runwayColors.primary : antGrey.grey7,
                                marginRight: 8,
                            }}
                        />
                    )}
                    {showManualUpdate && (
                        <Tooltip title={manualUpdateText} zIndex={100000}>
                            <UserOutlined style={{ color: antGrey.grey7, marginRight: 8 }} />
                        </Tooltip>
                    )}
                    {shouldDisplayEndDate && (
                        <Text
                            style={{
                                marginRight: 10,
                                color: antGrey.grey7,
                            }}
                        >
                            From:
                        </Text>
                    )}
                    <Text
                        strong
                        style={{
                            marginRight: 5,
                            color:
                                props.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ?
                                    antGrey.grey7
                                :   antGrey.grey9,
                            textDecoration:
                                (
                                    props.isSkippedRelease &&
                                    (props.eventDateType ===
                                        ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
                                        props.eventDateType ===
                                            ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
                                ) ?
                                    'line-through'
                                :   '',
                        }}
                    >
                        {formatDateString(props.eventDate, 'dddd, MMM D, YYYY')}
                    </Text>
                    <Divider type="vertical" style={{ height: '15px', width: '5px', borderColor: antGrey.grey7 }} />
                    <Text
                        style={{
                            color:
                                props.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ?
                                    antGrey.grey7
                                :   antGrey.grey9,
                            textDecoration:
                                (
                                    props.isSkippedRelease &&
                                    (props.eventDateType ===
                                        ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate ||
                                        props.eventDateType ===
                                            ReleaseCalendarEventType.ReleaseCalendarEventTypeTargetDate)
                                ) ?
                                    'line-through'
                                :   '',
                        }}
                    >
                        {formattedStartDateHour}
                    </Text>
                </div>
                {shouldDisplayEndDate && (
                    <div style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 5, paddingBottom: 10 }}>
                        {icon && (
                            <Icon
                                component={icon}
                                style={{
                                    color: antGrey.grey7,
                                    marginRight: 8,
                                }}
                            />
                        )}
                        <Text
                            style={{
                                marginRight: 10,
                                color: antGrey.grey7,
                            }}
                        >
                            To:
                        </Text>
                        <Text
                            strong
                            style={{
                                marginRight: 5,
                                color: antGrey.grey9,
                                textDecoration: '',
                            }}
                        >
                            {formatDateString(props.eventEndDate, 'dddd, MMM D, YYYY')}
                        </Text>
                        <Divider type="vertical" style={{ height: '15px', width: '5px', borderColor: antGrey.grey7 }} />
                        <Text
                            style={{
                                color: antGrey.grey9,
                                textDecoration: '',
                            }}
                        >
                            {formattedEndDateHour}
                        </Text>
                    </div>
                )}
                {description && (
                    <div style={{ paddingLeft: 10, paddingRight: 10, paddingBottom: 10, color: antGrey.grey7 }}>
                        {description}
                        {props.isExternalEvent && (
                            <div>
                                {props.externalEventDescription ?
                                    <div
                                        style={{
                                            backgroundColor: antGrey.grey4,
                                            padding: '8px 10px 8px 10px',
                                            marginTop: 10,
                                            marginBottom: 10,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <MarkdownComponent
                                            style={{
                                                color: 'inherit',
                                                fontSize: 'inherit',
                                            }}
                                            source={props.externalEventDescription}
                                            rehypePlugins={[rehypeRaw]}
                                        />
                                    </div>
                                :   <div style={{ height: 6 }} />}
                                <div>Created by: {props.organizer}</div>
                            </div>
                        )}
                        {isAcceleratedRollout && <InfoCircleOutlined style={{ color: antGrey.grey6, marginLeft: 8 }} />}
                    </div>
                )}
            </>
        );
    };

    return (
        <Popover
            content={content}
            title={title}
            arrow={false}
            overlayStyle={{
                padding: 0,
                margin: 0,
                zIndex: 99999,
            }}
            overlayClassName="release-cycle-popover"
        >
            {props.eventContent}
        </Popover>
    );
};

export default ReleaseCalendarEventPopover;
